<script setup lang="ts">
import type { ProductPageQuery } from '@/shared/apollo-api/__generated__/types';
import type { IInstallment } from '../../models/installment';
import type { IBadge } from '../../models/badge';
import type { IBanner } from '../../models/banner';

import BnplButton from '@/features/product-page/ui/common/bnpl-button/bnpl-button.vue';
import Installments from './installments.vue';
import BestPrice from './best-price.vue';
import AddCart from './add-cart.vue';
import Banners from './banners.vue';
import Price from './price.vue';
import Delivery from './delivery.vue';

import { useProductPage } from '@/features/product-page/business/use-product-page';
import { FeatureFlags } from '@/features/feature-flags';
import { useIntersectionObserver } from '@vueuse/core';
import { ref, computed } from 'vue';

// TODO: Replace with actual ShoppingDayBanner component when available
// Search results show this component doesn't exist in the project
const ShoppingDayBanner = {
  template: '<div class="shopping-day-banner-stub">Shopping Day Banner Stub</div>',
};

// Get product page state
const { state } = useProductPage();

withDefaults(
  defineProps<{
    sellPrice: number;
    fullPrice?: number;
    badges?: IBadge[];
    discountTimer?: NonNullable<ProductPageQuery['product']['skuList'][0]['discountTimer']>;
    isBestPrice?: boolean;
    installments: IInstallment[];
    availableAmount: number;
    isOneClickShown: boolean;
    banners: IBanner[] | null;
  }>(),
  {
    isOneClickShown: true,
  }
);

const emits = defineEmits<{
  (e: 'onChangeAddButtonVisibility', value: boolean): void;
}>();

const addToCartEl = ref<HTMLElement>();

useIntersectionObserver(addToCartEl, ([{ isIntersecting }]) => {
  // no reaction if button not over the viewport
  const top = addToCartEl.value?.getBoundingClientRect().top;
  const isShown = isIntersecting || (top !== undefined && top > 0);

  emits('onChangeAddButtonVisibility', isShown);
});

const isBuyingDayBannerShown = ref(false);

const currentSku = computed(() => state.currentSku);
const showBanners = computed(() => Number(currentSku.value?.availableAmount) > 0);

FeatureFlags.PRODUCT_BANNER().then((isShown: boolean) => {
  isBuyingDayBannerShown.value = isShown;
});
</script>

<template>
  <div class="call-to-action">
    <div v-if="isBuyingDayBannerShown" class="shopping-day-banner">
      <shopping-day-banner
        link="https://gift.promo.kazanexpress.ru/"
        page-name="PRODUCT"
        text="Оформите заказ от 1000 ₽ — участвуйте в мегааарозыгрыше"
      />
    </div>
    <Price
      :show-banners="showBanners"
      :sellPrice="sellPrice"
      :fullPrice="fullPrice"
      :badges="badges"
      :discount-timer="discountTimer"
    />
    <BestPrice v-if="isBestPrice" />
    <Installments v-if="false" :installments="installments" />
    <BnplButton v-if="showBanners" />

    <div ref="addToCartEl">
      <AddCart :availableAmount="availableAmount" />
    </div>

    <Banners v-if="showBanners" :banners="banners" />
    <Delivery v-if="banners && banners.length && showBanners" />
  </div>
</template>

<style lang="stylus" scoped>
.call-to-action
  background #FFFFFF
  box-shadow: 0 6px 12px -4px rgba(31, 32, 39, 0.04), 0 12px 24px -6px rgba(31, 32, 39, 0.08);
  border-radius 20px
  padding 24px 20px 32px
  height max-content
  .best-price
    margin-top 16px
    padding-bottom 4px
  .installments
    margin-top 16px
    padding-bottom 8px
  .add-cart
    margin-top 20px
  .one-click
    width 100%
    margin-top 12px
  .banners
    margin 16px 0

  .ui-button.secondary
    --bg-color: #F5F6FA;

.shopping-day-banner
  margin-bottom 20px
</style>
